import { useCallback, useEffect, useState } from 'react'
import { KeyRound } from 'lucide-react'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { EmptyRow, ErpTable, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { listSubjects, type Subject } from '@/features/dossier/api'
import { Pill, fmtNum } from '@/features/dossier/components/primitives'
import { listAccounts, listPersonas, listPlatforms, saveAccount, updateAccount,
         type Account, type Persona, type Platform } from './api'
import { PlatformChip } from './components'

/**
 * The accounts we post from, and the voices they post in.
 *
 * ## No credential ever appears on this page
 *
 * An account here is a HANDLE plus a pointer at an `integration` row. The token lives where
 * every other credential in this system lives, behind the one HTTP door that rate-limits and
 * records. A war room that kept its own copy of a platform token would be a second, unaudited
 * place for a secret to leak from, and there is no reason for it to exist.
 *
 * ## The daily cap is a safety rail, not a quota
 *
 * A room that can post 400 times a day is a room that can be embarrassing 400 times before
 * anybody notices. The cap is per-account and per-day, and hitting it refuses the approval
 * rather than queuing it — the operator should find out at the moment of decision.
 *
 * ## Voices are functions, not disguises
 *
 * Each persona says what it is FOR, and that note is shown beside every draft written in it. A
 * persona exists so a fact-check does not read like a consolation and a consolation does not
 * read like a fact-check — not so a reader is deceived about how many people are talking.
 */
/**
 * Human names for the config keys the drivers declare.
 *
 * A key absent from here still renders — as its raw key. That is deliberate: a driver added
 * later must not have to touch this file to become usable, and a slightly ugly label is a far
 * smaller problem than a field that does not appear at all.
 */
const CONFIG_LABELS: Record<string, string> = {
  page_id: 'Facebook Page id',
  ig_user_id: 'Instagram Business account id',
}

const CONFIG_HINTS: Record<string, string> = {
  page_id: '1234567890 — from the Page, not its @name',
  ig_user_id: '17841400000000000 — the IG Business id, not the @handle',
}

/**
 * Declared config keys this account has not filled in.
 *
 * Worth surfacing on the row rather than only failing at poll time: a Facebook account with no
 * page id looks completely healthy here — active, credential attached — and fails on the first
 * poll with an error nobody is watching for. Silent until it matters is the failure mode this
 * whole feature is built to avoid.
 */
function missingConfig(account: Account, platforms: Platform[]): string[] {
  const platform = platforms.find((p) => p.code === account.providerCode)
  if (!platform) return []
  return platform.accountConfigKeys.filter((key) => !(account.config ?? {})[key])
}

export function AccountsPage() {
  const [accounts, setAccounts] = useState<Account[] | null>(null)
  const [personas, setPersonas] = useState<Persona[]>([])
  const [platforms, setPlatforms] = useState<Platform[]>([])
  const [subjects, setSubjects] = useState<Subject[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [adding, setAdding] = useState(false)

  const [providerCode, setProviderCode] = useState('')
  const [handle, setHandle] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [personaId, setPersonaId] = useState('')
  const [subjectId, setSubjectId] = useState('')
  const [integrationId, setIntegrationId] = useState('')
  const [dailyCap, setDailyCap] = useState(25)
  /**
   * Per-account platform config, keyed exactly as the driver declares it. Held for every
   * platform at once rather than reset on switch: an operator comparing Facebook and Instagram
   * before saving should not lose the page id they already typed by looking at the other.
   * `create` sends only the chosen platform's keys, so nothing else can leak into the row.
   */
  const [config, setConfig] = useState<Record<string, string>>({})

  const load = useCallback(async () => {
    try {
      setError(null)
      setAccounts(await listAccounts())
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => { void load() }, [load])
  useEffect(() => {
    void listPersonas().then(setPersonas).catch(() => setPersonas([]))
    void listPlatforms().then((rows) => {
      setPlatforms(rows)
      setProviderCode((c) => c || rows.find((p) => p.canPost)?.code || rows[0]?.code || '')
    }).catch(() => setPlatforms([]))
    void listSubjects({ size: '100' }).then(setSubjects).catch(() => setSubjects([]))
  }, [])

  if (error && !accounts) return <ErrorStrip message={error} />
  if (!accounts) return <LoadingBlock label="Loading accounts" />

  const chosen = platforms.find((p) => p.code === providerCode)

  async function create(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true); setError(null)
    try {
      await saveAccount({
        providerCode, handle: handle.trim().replace(/^@/, ''),
        displayName: displayName.trim() || null,
        personaId: personaId || null, subjectId: subjectId || null,
        integrationId: integrationId.trim() || null, dailyCap,
        // Only the chosen platform's declared keys. The server rejects any other key outright,
        // so sending the whole map would turn a stale field into a failed save.
        config: Object.fromEntries(
          (chosen?.accountConfigKeys ?? [])
            .map((key) => [key, (config[key] ?? '').trim()])
            .filter(([, value]) => value !== ''),
        ),
      })
      setAdding(false); setHandle(''); setDisplayName(''); setIntegrationId(''); setConfig({})
      await load()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Accounts & voices"
        description="Where replies go out from, and in what voice. Tokens are not stored here —
                     an account points at an integration, and that is the only network door."
      />

      {error && <ErrorStrip message={error} />}

      <Panel>
        <div className="flex flex-wrap items-center justify-between gap-2 p-2">
          <p className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <KeyRound className="size-3.5" />
            Configure the platform credential under Integrations first, then point an account
            at it here.
          </p>
          <button type="button" onClick={() => setAdding((v) => !v)}
                  className="rounded border border-border px-2 py-1 text-[11px]">
            {adding ? 'Close' : 'Add an account'}
          </button>
        </div>

        {adding && (
          <form onSubmit={create} className="space-y-2 border-t border-border p-3">
            <div className="grid gap-2 sm:grid-cols-3">
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Platform
                </span>
                <select value={providerCode} onChange={(e) => setProviderCode(e.target.value)}
                        className="h-7 rounded border border-input bg-background px-2
                                   text-[12px]">
                  {platforms.map((p) => (
                    <option key={p.code} value={p.code}>
                      {p.label}
                      {p.canPost ? '' : p.code === 'MANUAL' ? ' — posted by hand' : ' — cannot post'}
                    </option>
                  ))}
                </select>
                {/*
                  MANUAL is not disabled, and that is deliberate. A deployment on a MANUAL
                  account sends no HTTP request: it freezes the approved text, records the
                  approver, and somebody copies it out. That is still a deployment, because
                  what the ledger is for is knowing what the campaign said in public — not
                  which client sent it. It is also the only way to run the whole chain before
                  any platform credential exists.
                */}
                {chosen && !chosen.canPost && (
                  <span className="text-[10px] text-amber-700 dark:text-amber-400">
                    {chosen.code === 'MANUAL'
                      ? 'Approved replies are recorded and copied out by a person. The ledger '
                        + 'holds the exact text either way.'
                      : 'We can read this platform but not reply on it.'}
                  </span>
                )}
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Handle
                </span>
                <input value={handle} onChange={(e) => setHandle(e.target.value)} required
                       placeholder="@campaign_desk"
                       className="h-7 rounded border border-input bg-background px-2
                                  text-[12px]" />
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Shown as
                </span>
                <input value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                       className="h-7 rounded border border-input bg-background px-2
                                  text-[12px]" />
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Voice
                </span>
                <select value={personaId} onChange={(e) => setPersonaId(e.target.value)}
                        className="h-7 rounded border border-input bg-background px-2
                                   text-[12px]">
                  <option value="">Chosen per draft</option>
                  {personas.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Works for
                </span>
                <select value={subjectId} onChange={(e) => setSubjectId(e.target.value)}
                        className="h-7 rounded border border-input bg-background px-2
                                   text-[12px]">
                  <option value="">Any subject</option>
                  {subjects.map((s) => (
                    <option key={s.id} value={s.id}>{s.displayName}</option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Replies a day
                </span>
                <input type="number" min={1} max={500} value={dailyCap}
                       onChange={(e) => setDailyCap(Number(e.target.value))}
                       className="h-7 rounded border border-input bg-background px-2
                                  text-[12px]" />
                <span className="text-[10px] text-muted-foreground">
                  Reaching it refuses the next approval outright.
                </span>
              </label>
            </div>
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Integration id holding the credential
              </span>
              <input value={integrationId} onChange={(e) => setIntegrationId(e.target.value)}
                     placeholder="Leave empty to configure it later — the account will not post
                                  until it is set."
                     className="h-7 rounded border border-input bg-background px-2
                                text-[12px]" />
            </label>

            {/*
              What the driver says it needs beyond the credential. Rendered from
              `accountConfigKeys` rather than hard-coded per platform, so a new driver gets its
              fields here for free — which is the whole reason the interface declares them.

              These are identifiers, never secrets: a Page id and a channel id are in the public
              URL of the thing they name. The credential is the integration, and that is the
              only field on this page that touches one.
            */}
            {chosen && chosen.accountConfigKeys.length > 0 && (
              <div className="grid gap-2 rounded border border-border p-2 sm:grid-cols-2">
                <p className="sm:col-span-2 text-[10px] text-muted-foreground">
                  {chosen.label} needs to know which page or channel this account speaks for.
                  One credential often fronts several, so this lives on the account.
                </p>
                {chosen.accountConfigKeys.map((key) => (
                  <label key={key} className="flex flex-col gap-0.5">
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      {CONFIG_LABELS[key] ?? key}
                    </span>
                    <input value={config[key] ?? ''}
                           onChange={(e) =>
                             setConfig((c) => ({ ...c, [key]: e.target.value }))}
                           placeholder={CONFIG_HINTS[key] ?? key}
                           className="h-7 rounded border border-input bg-background px-2
                                      text-[12px]" />
                  </label>
                ))}
              </div>
            )}
            <button type="submit" disabled={busy || !handle.trim()}
                    className="rounded bg-primary px-3 py-1.5 text-[12px] font-medium
                               text-primary-foreground disabled:opacity-50">
              {busy ? 'Saving…' : 'Save the account'}
            </button>
          </form>
        )}
      </Panel>

      <Panel>
        <ErpTable head={<>
          <Th>Handle</Th>
          <Th>Platform</Th>
          <Th className="axis-hide-sm">Voice</Th>
          <Th>Today</Th>
          <Th>State</Th>
        </>}>
          {accounts.length === 0 && (
            <EmptyRow colSpan={5}
                      message="No account configured. Drafts can still be written and approved —
                               they simply have nowhere to go." />
          )}
          {accounts.map((a) => (
            <tr key={a.id}>
              <Td>
                <span className="font-medium">@{a.handle}</span>
                {a.displayName && (
                  <span className="block text-[10px] text-muted-foreground">
                    {a.displayName}
                  </span>
                )}
                {a.integrationId == null && (
                  <span className="block text-[10px] text-amber-700 dark:text-amber-400">
                    no credential attached
                  </span>
                )}
                {Object.entries(a.config ?? {}).map(([key, value]) => (
                  <span key={key} className="block text-[10px] text-muted-foreground">
                    {CONFIG_LABELS[key] ?? key}: {value}
                  </span>
                ))}
                {missingConfig(a, platforms).map((key) => (
                  <span key={key}
                        className="block text-[10px] text-amber-700 dark:text-amber-400">
                    no {CONFIG_LABELS[key] ?? key} — this account cannot poll or post
                  </span>
                ))}
              </Td>
              <Td><PlatformChip code={a.providerCode} /></Td>
              <Td className="axis-hide-sm">
                {a.personaName
                  ? <Pill className="border-sky-500/30 bg-sky-500/10 text-sky-700
                                     dark:text-sky-400">{a.personaName}</Pill>
                  : <span className="text-[10px] text-muted-foreground">per draft</span>}
              </Td>
              <Td>
                <span className="text-[11px]">
                  {fmtNum(a.postedToday)} / {fmtNum(a.dailyCap)}
                </span>
              </Td>
              <Td>
                {/*
                  The WHOLE row, not `{ active }`.

                  `POST /accounts/{id}` is a full replace: it overwrites every column from the
                  body, and `providerCode` and `handle` are @NotBlank. A partial body was
                  therefore rejected outright — the stand-down button did nothing but raise a
                  400 — and had it been accepted it would have blanked the display name, the
                  voice, the subject and (since this change) the platform config, on the way to
                  flipping one boolean.
                */}
                <button type="button"
                        onClick={() => {
                          setBusy(true)
                          void updateAccount(a.id, {
                            providerCode: a.providerCode,
                            integrationId: a.integrationId,
                            handle: a.handle,
                            displayName: a.displayName,
                            personaId: a.personaId,
                            subjectId: a.subjectId,
                            dailyCap: a.dailyCap,
                            config: a.config,
                            active: !a.active,
                          })
                            .then(load)
                            .catch((err) => setError(explain(err)))
                            .finally(() => setBusy(false))
                        }}
                        className="rounded border border-border px-2 py-0.5 text-[10px]">
                  {a.active ? 'Active — stand down' : 'Stood down — activate'}
                </button>
                {a.lastError && (
                  <span className="block text-[10px] text-rose-600">{a.lastError}</span>
                )}
              </Td>
            </tr>
          ))}
        </ErpTable>
      </Panel>

      <Panel>
        <div className="space-y-1.5 p-3">
          <p className="text-[11px] font-medium">The voices</p>
          <p className="text-[10px] text-muted-foreground">
            Each is a function. The guardrail line is part of the system prompt the council is
            given, not a note to ourselves.
          </p>
          <ul className="grid gap-2 sm:grid-cols-3">
            {personas.map((p) => (
              <li key={p.id} className="rounded border border-border p-2">
                <p className="text-[11px] font-medium">{p.name}</p>
                <p className="text-[10px] text-muted-foreground">{p.functionNote}</p>
                {p.guardrails && (
                  <p className="mt-1 text-[10px] text-amber-700 dark:text-amber-400">
                    {p.guardrails}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      </Panel>
    </div>
  )
}
